import { EventParams } from '../../MathFactsGenerator/worksheetEventParams.ts';
import { DropGameResult } from './DropGameRun.tsx';
import { ChartProgress } from './chartGame.ts';
import { ProductGameResult } from './ProductGame.tsx';
import { MunchResult } from './MunchGame.tsx';
import { CollieState } from './collieGame.ts';
import { Kick } from './soccerGame.ts';
import { PlateResult } from './PlateGameRun.tsx';
import { FootballResult } from './FootballGameRun.tsx';
import { RunnerResult } from './RunnerGameRun.tsx';
import { MergeResult } from './mergeGame.ts';
import { ShopResult } from './shopGame.ts';
import { firstTryCount, Tile, wrongTries } from './pictureGame.ts';
import { CrosswordResult } from './crosswordGame.ts';

/*
 * The drop game reports through the quiz's `quiz_completed` event with its own
 * `mode` (ADR 0053 §7), so it needs no new GA4 registrations. `skipped` carries
 * the facts that landed - the nearest existing meaning, "never answered".
 * `duration_ms` is left out: on the quiz it is the chosen length, and play time
 * under the same name would corrupt its average. `operation` is what was
 * played, comma-joined like soccer's, because the page lets a child mix
 * operations (ADR 0098 §1).
 */
export function gameEventParams(operations: string[], result: DropGameResult): EventParams {
    return {
        operation: operations.join(','),
        mode: 'drop_game',
        correct: result.cleared,
        wrong: result.wrongGuesses,
        skipped: result.landed.length,
    };
}

/*
 * The chart game reports once, when a chart is completed (ADR 0054 §5):
 * `correct` is the whole chart and `wrong` the wrong answers given filling it.
 */
export function chartGameEventParams(operation: string, progress: ChartProgress): EventParams {
    return {
        operation,
        mode: 'chart_game',
        correct: progress.filled.length,
        wrong: progress.wrong,
        skipped: 0,
    };
}

/*
 * The Product Game reports at game over (ADR 0055 §5). Only the human answers
 * are counted; who won is deliberately not sent - it would need a new
 * dimension, and ADR 0026 asks for a question before a parameter.
 */
export function productGameEventParams(operation: string, result: ProductGameResult): EventParams {
    return {
        operation,
        mode: 'product_game',
        correct: result.correct,
        wrong: result.wrong,
        skipped: 0,
    };
}

/*
 * Math Munch reports at game over (ADR 0056 §5): squares munched and wrong
 * munches. Being caught by a monster is not an answer, so it is not sent.
 * `operation` is what was played, comma-joined like Soccer's (ADR 0098 §4).
 */
export function munchGameEventParams(operations: string[], result: MunchResult): EventParams {
    return {
        operation: operations.join(','),
        mode: 'munch_game',
        correct: result.munched,
        wrong: result.wrong,
        skipped: 0,
    };
}

/*
 * Collie Crossing has no game over, so it reports once per crossing (ADR 0057
 * §6): tiles stepped on safely, and falls on that floor. `operation` is what
 * was played, comma-joined like Soccer's (ADR 0098 §4).
 */
export function collieGameEventParams(operations: string[], state: CollieState): EventParams {
    return {
        operation: operations.join(','),
        mode: 'collie_game',
        correct: state.safeSteps,
        wrong: state.falls,
        skipped: 0,
    };
}

/*
 * Math Facts Soccer reports at full time (ADR 0059 §6). `correct` is right
 * answers - goals and saves both, since a save was right but slow - so the
 * three counts add up to the ten kicks, as a quiz's do. `operation` is what
 * was played, comma-joined like a multi-operator worksheet's, because this
 * page lets a child mix operations and the route's is only where it starts.
 */
export function soccerGameEventParams(operations: string[], kicks: Kick[]): EventParams {
    const count = (test: (k: Kick) => boolean) => kicks.filter(test).length;
    return {
        operation: operations.join(','),
        mode: 'soccer_game',
        correct: count((k) => k.outcome === 'goal' || k.outcome === 'saved'),
        wrong: count((k) => k.outcome === 'wide'),
        skipped: count((k) => k.outcome === 'skipped'),
    };
}

/*
 * Plate Smash reports when the bell goes (ADR 0060 §7): first-shot hits and
 * wrong plates. The score, the difficulty and above all the leaderboard name
 * are not sent - the name never leaves the browser (§6).
 */
export function platesGameEventParams(operations: string[], result: PlateResult): EventParams {
    return {
        operation: operations.join(','),
        mode: 'plates_game',
        correct: result.hits,
        wrong: result.wrongPlates,
        skipped: 0,
    };
}

/*
 * Math Facts Football reports when the fourth drive ends (ADR 0103). `correct`
 * is passes caught, `wrong` is passes thrown to the wrong receiver and
 * `skipped` is sacks - a fact never answered before the rush arrived, as the
 * quiz's skips are. The points and the defense are not sent.
 */
export function footballGameEventParams(operations: string[], result: FootballResult): EventParams {
    return {
        operation: operations.join(','),
        mode: 'football_game',
        correct: result.completions,
        wrong: result.wrongThrows,
        skipped: result.sacks,
    };
}

/*
 * Math Runner reports when the third heart goes (ADR 0124). `correct` is gates
 * passed, `wrong` is rows that ended on a wrong gate and `skipped` is rows that
 * arrived with no lane picked - a fact never answered, as the quiz's skips are.
 * The score and the pace are not sent.
 */
export function runnerGameEventParams(operations: string[], result: RunnerResult): EventParams {
    return {
        operation: operations.join(','),
        mode: 'runner_game',
        correct: result.score,
        wrong: result.wrongGates,
        skipped: result.noPicks,
    };
}

/*
 * Factor Merge reports when a run ends (ADR 0128 §5, §7). `correct` is targets made
 * and `wrong` is merges refused; there is no skipping. The grade stands in
 * `operation` (`grade_3` to `grade_8`), as it does for Shop Rush (ADR 0131 §7),
 * because the page has no operations to choose. Which tiles were merged is not sent.
 */
export function mergeGameEventParams(result: MergeResult): EventParams {
    return {
        operation: `grade_${result.level.slice(1)}`,
        mode: 'merge_game',
        correct: result.targetsMade,
        wrong: result.refusedMerges,
        skipped: 0,
    };
}

/*
 * Shop Rush reports when the tenth customer has been seen (ADR 0131 §5). `correct`
 * is orders served, on the first try or the second; `wrong` is wrong tries; and
 * `skipped` is customers who ran out of patience - an order never answered, as the
 * quiz's skips are. The grade stands in `operation` (`grade_2` to `grade_8`, ADR 0131 §7),
 * as the level does for Factor Merge, because the page has no operations to choose.
 * The stars and the pace are not sent.
 */
export function shopGameEventParams(result: ShopResult): EventParams {
    return {
        operation: `grade_${result.level.slice(1)}`,
        mode: 'shop_game',
        correct: result.ordersServed,
        wrong: result.wrongTries,
        skipped: result.walkedOut,
    };
}

/*
 * Hidden Picture reports when the last tile lifts (ADR 0133 §4). `correct` is tiles
 * right the first time, `wrong` is wrong tries, and there is no skipping: an empty
 * answer does nothing. `operation` is what was played, comma-joined like Soccer's
 * (ADR 0098 §4). The picture is not sent: it follows the date, which the event
 * already carries.
 */
export function pictureGameEventParams(operations: string[], tiles: Tile[]): EventParams {
    return {
        operation: operations.join(','),
        mode: 'picture_game',
        correct: firstTryCount(tiles),
        wrong: wrongTries(tiles),
    };
}

/*
 * The Baker Street Math Crossword reports when the fifth case is closed (ADR 0134 §5).
 * `correct` is blanks the child filled in right, `wrong` is wrong blanks that Check found,
 * and `skipped` is blanks Ask Holmes filled - numbers the child did not work out, as the
 * quiz's skips are. The rank stands in `operation`, as the level does for Shop Rush. The
 * glasses are not sent.
 */
export function crosswordGameEventParams(result: CrosswordResult): EventParams {
    return {
        operation: result.rank,
        mode: 'crossword_game',
        correct: result.correct,
        wrong: result.wrong,
        skipped: result.skipped,
    };
}
